import axios from 'axios';
import React from 'react';

/**
 * ====================================================================
 * XTREAM CODES API SERVICE - SAFE OPTIMIZATIONS
 * ====================================================================
 * Mevcut sistemi bozmadan, güvenli performans iyileştirmeleri
 * Geriye uyumlu, progressif enhancement yaklaşımı
 * ====================================================================
 */

// API Konfigürasyonu (Mevcut ayarlar korundu + iyileştirmeler)
const API_CONFIG = {
  BASE_URL: 'https://tivigo.store/endpoint.php',
  TIMEOUT: 15000, // 10'dan 15'e çıkarıldı (daha stabil)
  CACHE_DURATION: 5 * 60 * 1000, // 5 dakika cache (korundu)
  TOKEN_REFRESH_THRESHOLD: 60 * 60 * 1000, // 1 saat (korundu)
  // YENİ: Güvenli optimizasyon ayarları
  RETRY_ATTEMPTS: 2, // Başarısız istekler için 2 deneme
  RETRY_DELAY: 1000, // 1 saniye bekleme
  BACKGROUND_SYNC_INTERVAL: 60000, // 1 dakikada bir background kontrol
};

// Gelişmiş Cache Sistemi (Mevcut cache sistemi korundu + iyileştirmeler)
class SafeApiCache {
  constructor() {
    // Mevcut cache sistemi korundu
    this.cache = new Map();
    
    // YENİ: Güvenli eklemeler
    this.stats = {
      hits: 0,
      misses: 0,
      lastCleanup: Date.now()
    };
    
    // YENİ: Otomatik temizlik (5 dakikada bir)
    this.startAutoCleanup();
  }

  // Mevcut set metodu korundu + iyileştirmeler
  set(key, data, duration = API_CONFIG.CACHE_DURATION) {
    const expiry = Date.now() + duration;
    this.cache.set(key, { 
      data, 
      expiry,
      lastAccess: Date.now() // YENİ: Son erişim takibi
    });
    this.stats.sets++;
  }

  // Mevcut get metodu korundu + iyileştirmeler
  get(key) {
    const item = this.cache.get(key);
    if (!item) {
      this.stats.misses++;
      return null;
    }
    
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }
    
    // YENİ: Erişim zamanı güncelle
    item.lastAccess = Date.now();
    this.stats.hits++;
    return item.data;
  }

  // Mevcut metotlar korundu
  clear() {
    this.cache.clear();
  }

  delete(key) {
    this.cache.delete(key);
  }

  // YENİ: Güvenli otomatik temizlik
  startAutoCleanup() {
    setInterval(() => {
      const now = Date.now();
      const oldItems = [];
      
      for (const [key, item] of this.cache.entries()) {
        // 10 dakikadan eski ve expired olan itemları temizle
        if (now - item.lastAccess > 10 * 60 * 1000 && now > item.expiry) {
          oldItems.push(key);
        }
      }
      
      oldItems.forEach(key => this.cache.delete(key));
      this.stats.lastCleanup = now;
    }, 5 * 60 * 1000); // 5 dakikada bir
  }

  // YENİ: Cache istatistikleri
  getStats() {
    return {
      ...this.stats,
      size: this.cache.size,
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0
    };
  }
}

// Global cache instance (Mevcut yapı korundu)
const cache = new SafeApiCache();

// Mevcut Axios instance korundu + iyileştirmeler
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Mevcut TokenManager korundu + güvenli iyileştirmeler
class TokenManager {
  static TOKEN_KEY = 'xtream_api_token';
  static TOKEN_EXPIRES_KEY = 'xtream_token_expires';

  // Mevcut metotlar korundu
  static setToken(token, expiresIn = 86400) {
    localStorage.setItem(this.TOKEN_KEY, token);
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.TOKEN_EXPIRES_KEY, expiryTime.toString());
  }

  static getToken() {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const expiry = localStorage.getItem(this.TOKEN_EXPIRES_KEY);
    
    if (!token || !expiry) return null;
    
    if (Date.now() > parseInt(expiry)) {
      this.clearToken();
      return null;
    }
    
    return token;
  }

  static clearToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRES_KEY);
  }

  static isTokenExpiringSoon() {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRES_KEY);
    if (!expiry) return true;
    
    const timeLeft = parseInt(expiry) - Date.now();
    return timeLeft < API_CONFIG.TOKEN_REFRESH_THRESHOLD;
  }

  // YENİ: Token süresini dakika cinsinden getir
  static getTokenTimeLeft() {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRES_KEY);
    if (!expiry) return 0;
    
    const timeLeft = parseInt(expiry) - Date.now();
    return Math.max(0, Math.floor(timeLeft / (1000 * 60))); // Dakika cinsinden
  }

  // YENİ: Token durumunu al (App.js için)
  static getTokenStatus() {
    const token = this.getToken();
    const expiry = localStorage.getItem(this.TOKEN_EXPIRES_KEY);
    
    if (!token || !expiry) {
      return { isValid: false, isExpired: true, isExpiringSoon: true };
    }
    
    const timeLeft = parseInt(expiry) - Date.now();
    const isExpired = timeLeft <= 0;
    const isExpiringSoon = timeLeft < API_CONFIG.TOKEN_REFRESH_THRESHOLD;
    
    return {
      isValid: !isExpired,
      isExpired,
      isExpiringSoon,
      timeLeftMs: Math.max(0, timeLeft),
      timeLeftMinutes: Math.max(0, Math.floor(timeLeft / (1000 * 60)))
    };
  }
}

// YENİ: Güvenli retry mekanizması
const safeRetry = async (fn, attempts = API_CONFIG.RETRY_ATTEMPTS) => {
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      // Son deneme ise hatayı fırlat
      if (i === attempts - 1) throw error;
      
      // Network hatası veya 5xx server hatası ise retry yap
      if (!error.response || error.response.status >= 500) {
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY * (i + 1)));
        continue;
      }
      
      // 4xx client hatası ise retry yapma
      throw error;
    }
  }
};

// Mevcut interceptors korundu + iyileştirmeler
apiClient.interceptors.request.use(
  (config) => {
    const token = TokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // YENİ: Request timestamp ekle (debugging için)
    config.metadata = { startTime: Date.now() };
    
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    // YENİ: Response time logging (sadece development'ta)
    if (process.env.NODE_ENV === 'development' && response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      if (duration > 3000) { // 3 saniyeden uzun sürerse log'la
      }
    }
    return response;
  },
  async (error) => {
    // ✅ 401 HATASI - Token expire/invalid
    if (error.response?.status === 401) {
      console.warn('⚠️ 401 Unauthorized - Token geçersiz');
      
      // Token'ı temizle
      TokenManager.clearToken();
      
      // ❌ Artık otomatik logout YAPMA
      // ❌ window.location.href = '/login' kaldırıldı
      // ✅ App.js'deki smartRefreshToken otomatik devreye girecek
      
      return Promise.reject(new Error('Token geçersiz'));
    }
    
    // ✅ 403 HATASI - Forbidden
    if (error.response?.status === 403) {
      console.warn('⚠️ 403 Forbidden - Erişim engellendi');
      return Promise.reject(new Error('Bu işlem için yetkiniz yok'));
    }
    
    // Network hatası
    if (!error.response) {
      return Promise.reject(new Error('Bağlantı hatası, lütfen internet bağlantınızı kontrol edin'));
    }
    
    const message = error.response.data?.message || 'Bilinmeyen hata oluştu';
    return Promise.reject(new Error(message));
  }
);

// Mevcut XtreamAPI class'ı korundu + güvenli iyileştirmeler
class XtreamAPI {
  
  // Mevcut test metodu korundu
  static async test() {
    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { endpoint: 'test' }
      });
      return response.data;
    });
  }

  // Mevcut login metodu korundu + iyileştirmeler
  static async login(username, password) {
    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { 
          endpoint: 'login',
          username,
          password
        }
      });

      if (response.data.success) {
        const { token, expires_in } = response.data.data;
        TokenManager.setToken(token, expires_in);
        cache.clear(); // Login sonrası cache temizle
        
        // YENİ: Login başarılı logı
      }

      return response.data;
    });
  }

  // Mevcut getUserData metodu korundu + iyileştirmeler
static async getUserData(useCache = true) {
  const cacheKey = 'user_data';
  
  if (useCache) {
    const cached = cache.get(cacheKey);
    if (cached) return cached;
  }

  return safeRetry(async () => {
    const token = TokenManager.getToken();
    const response = await fetch(`https://tivigo.store/endpoint.php?endpoint=user_data&token=${token}`);
    const data = await response.json();

    if (data.success && useCache) {
      cache.set(cacheKey, data, 2 * 60 * 1000);
    }

    return data;
  });
}

  // Mevcut changePassword metodu korundu
  static async changePassword(currentPassword, newPassword) {
    return safeRetry(async () => {
      const response = await apiClient.post('', {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        params: { endpoint: 'change_password' }
      });

      if (response.data.success) {
        cache.delete('user_data');
      }

      return response.data;
    });
  }

  // Mevcut updateContact metodu korundu
  static async updateContact(phone, email) {
    return safeRetry(async () => {
      const response = await apiClient.post('', {
        phone,
        email
      }, {
        params: { endpoint: 'update_contact' }
      });

      if (response.data.success) {
        cache.delete('user_data');
      }

      return response.data;
    });
  }

  // Mevcut reseller notes metotları korundu
  static async getResellerNotes() {
    const cacheKey = 'reseller_notes';
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { endpoint: 'reseller_notes' }
      });

      if (response.data.success) {
        cache.set(cacheKey, response.data);
      }

      return response.data;
    });
  }

  static async setResellerNotes(notes) {
    return safeRetry(async () => {
      const response = await apiClient.post('', {
        notes
      }, {
        params: { endpoint: 'reseller_notes' }
      });

      if (response.data.success) {
        cache.delete('reseller_notes');
      }

      return response.data;
    });
  }

  // Mevcut bouquet metotları korundu
  static async manageBouquets(action, data = {}) {
    return safeRetry(async () => {
      const payload = { action, ...data };
      const response = await apiClient.post('', payload, {
        params: { endpoint: 'manage_bouquets' }
      });

      if (response.data.success) {
        cache.delete('user_data');
        cache.delete('bouquets_list');
      }

      return response.data;
    });
  }

  static async getBouquetsList() {
    const cacheKey = 'bouquets_list';
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { endpoint: 'list_bouquets' }
      });

      if (response.data.success) {
        // YENİ: Bouquet listesi için uzun cache (10 dakika)
        cache.set(cacheKey, response.data, 10 * 60 * 1000);
      }

      return response.data;
    });
  }

  // Mevcut activity metotları korundu
  static async getActivity(limit = 10, page = 1) {
    const cacheKey = `activity_${limit}_${page}`;
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { 
          endpoint: 'activity',
          limit,
          page
        }
      });

      if (response.data.success) {
        // YENİ: Activity için kısa cache (1 dakika)
        cache.set(cacheKey, response.data, 60 * 1000);
      }

      return response.data;
    });
  }

  static async getActivityNow() {
    // Real-time data, cache yok
    return safeRetry(async () => {
      const response = await apiClient.get('', {
        params: { endpoint: 'activity_now' }
      });
      return response.data;
    });
  }

  // Mevcut metotlar korundu
  static clearCache() {
    cache.clear();
  }

  static logout() {
    TokenManager.clearToken();
    cache.clear();
  }

  // YENİ: Güvenli utility metotları
  static getCacheStats() {
    return cache.getStats();
  }

  static getTokenInfo() {
    return {
      hasToken: !!TokenManager.getToken(),
      timeLeft: TokenManager.getTokenTimeLeft(),
      isExpiringSoon: TokenManager.isTokenExpiringSoon()
    };
  }

  static getTokenStatus() {
    return TokenManager.getTokenStatus();
  }

  // YENİ: Background health check (opsiyonel)
  static async healthCheck() {
    try {
      const result = await this.test();
      return result.success;
    } catch (error) {
      return false;
    }
  }

  // YENİ: User durumunu validate et (banned, expired, disabled kontrolü)
  static async validateUserStatus() {
    try {
      const userData = await this.getUserData(false); // Fresh data
      
      if (!userData.success || !userData.data || !userData.data.user) {
        return { valid: false, reason: 'user_data_failed' };
      }
      
      const user = userData.data.user;
      const now = Math.floor(Date.now() / 1000);
      
      // 3 KONTROL (öncelik sırasına göre)
      if (Number(user.admin_enabled) === 0) {
        return { valid: false, reason: 'banned', message: 'Hesabınız yönetici tarafından yasaklandı', user: userData.data };
      }
      
      if (user.exp_date <= now) {
        return { valid: false, reason: 'expired', message: 'Üyelik süreniz doldu', user: userData.data };
      }
      
      if (Number(user.enabled) === 0) {
        return { valid: false, reason: 'disabled', message: 'Hesabınız devre dışı bırakıldı', user: userData.data };
      }
      
      // ✅ Her şey OK
      return { valid: true, user: userData.data };
      
    } catch (error) {
      return { valid: false, reason: 'validation_error', error };
    }
  }
}

// Mevcut hooks korundu + iyileştirmeler
export const usePolling = (apiCall, interval = 30000, dependencies = []) => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    let timeoutId;

    const fetchData = async () => {
      try {
        if (mounted) {
          const result = await apiCall();
          if (mounted) {
            setData(result);
            setError(null);
            setLoading(false);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    const poll = () => {
      fetchData();
      if (mounted) {
        // YENİ: Tab aktif değilse polling'i yavaşlat
        const actualInterval = document.hidden ? interval * 3 : interval;
        timeoutId = setTimeout(poll, actualInterval);
      }
    };

    poll();

    // YENİ: Visibility change listener
    const handleVisibilityChange = () => {
      if (!document.hidden && mounted) {
        // Tab aktif olunca hemen refresh yap
        clearTimeout(timeoutId);
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, dependencies);

  return { data, loading, error };
};

// Mevcut useTokenStatus hook'u korundu + iyileştirmeler
export const useTokenStatus = () => {
  const [isTokenValid, setIsTokenValid] = React.useState(!!TokenManager.getToken());
  const [isExpiringSoon, setIsExpiringSoon] = React.useState(TokenManager.isTokenExpiringSoon());
  const [timeLeft, setTimeLeft] = React.useState(TokenManager.getTokenTimeLeft()); // YENİ

  React.useEffect(() => {
    const checkToken = () => {
      setIsTokenValid(!!TokenManager.getToken());
      setIsExpiringSoon(TokenManager.isTokenExpiringSoon());
      setTimeLeft(TokenManager.getTokenTimeLeft()); // YENİ
    };

    // YENİ: Dakikada bir token durumunu kontrol et
    const interval = setInterval(checkToken, 60000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkToken();
      }
    };

    const handleFocus = () => {
      checkToken();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval); // YENİ
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  return { isTokenValid, isExpiringSoon, timeLeft }; // YENİ: timeLeft eklendi
};

export default XtreamAPI;
